import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


/**
 * Component for displaying file content in a modal
 */
const FileModal = ({ 
  isVisible, 
  selectedFile, 
  fileContent, 
  fileContentType, 
  isLoading, 
  onClose 
}) => {

  const [pdfObjectUrl, setPdfObjectUrl] = React.useState(null);

  React.useEffect(() => {
    if (fileContentType === 'pdf' && fileContent instanceof Blob) {
      const url = URL.createObjectURL(fileContent);
      setPdfObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setPdfObjectUrl(null);
      };
    } else {
      setPdfObjectUrl(null);
    }
  }, [fileContentType, fileContent]);


  if (!isVisible) return null;

  const renderFileContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2">Loading file content...</div>
        </div>
      );
    }

    if (!fileContent) {
      return (
        <div className="text-center py-4 text-muted">
          No content available
        </div>
      );
    }

    // Render based on content type
    switch (fileContentType) {
      case 'json':
        return (
          <SyntaxHighlighter 
            language="json" 
            style={tomorrow}
            customStyle={{ 
              maxHeight: '500px', 
              fontSize: '12px',
              margin: 0
            }}
          >
            {typeof fileContent === 'string' ? fileContent : JSON.stringify(fileContent, null, 2)}
          </SyntaxHighlighter>
        );
      
      case 'xml':
        return (
          <SyntaxHighlighter 
            language="xml" 
            style={tomorrow}
            customStyle={{ 
              maxHeight: '500px', 
              fontSize: '12px',
              margin: 0
            }}
          >
            {fileContent}
          </SyntaxHighlighter>
        );
      
      case 'html':
        return (
          <SyntaxHighlighter 
            language="html" 
            style={tomorrow}
            customStyle={{ 
              maxHeight: '500px', 
              fontSize: '12px',
              margin: 0
            }}
          >
            {fileContent}
          </SyntaxHighlighter>
        );
      
      case 'log':
        return (
          <div 
            className="bg-dark text-light p-3 rounded"
            style={{ 
              maxHeight: '500px', 
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre-wrap'
            }}
          >
            {fileContent}
          </div>
        );

        case 'markdown':
          return (
            <div
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
                background: 'var(--bs-body-bg, #212529)',
                color: 'var(--bs-body-color, #f8f9fa)',
                padding: '1rem',
                borderRadius: '0.5rem',
                fontSize: '15px'
              }}
              className="markdown-body"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{fileContent}</ReactMarkdown>
            </div>
          );

        case 'pdf': {
          const url = pdfObjectUrl || (typeof fileContent === 'string' && fileContent.startsWith('http') ? fileContent : '');
          if (!url) return <div className="text-danger">Не удалось открыть PDF</div>;
          return (
            <iframe
              src={url}
              style={{ width: '100%', height: '80vh', border: 'none', borderRadius: '10px' }}
              title={selectedFile.name}
            />
          );
        }

      default:
        return (
          <div 
            className="border p-3 rounded"
            style={{ 
              maxHeight: '500px', 
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f8f9fa'
            }}
          >
            {fileContent}
          </div>
        );
    }
  };

  const getFileIcon = () => {
    switch (fileContentType) {
      case 'json': return 'fa-file-code';
      case 'xml': return 'fa-file-code';
      case 'html': return 'fa-file-code';
      case 'log': return 'fa-file-text';
      default: return 'fa-file';
    }
  };

  const downloadFile = () => {
    if (!fileContent || !selectedFile) return;
    
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedFile.name || 'file.txt';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <>
    <div className="modal-backdrop fade show" onClick={onClose} />
    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className={`fa ${getFileIcon()} me-2`}></i>
              {selectedFile?.name || 'File Content'}
              {selectedFile?.size && (
                <small className="text-muted ms-2">({selectedFile.size} bytes)</small>
              )}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          
          <div className="modal-body">
            {renderFileContent()}
          </div>
          
          <div className="modal-footer">
            <div className="me-auto">
              {selectedFile && (
                <small className="text-muted">
                  Type: {fileContentType?.toUpperCase() || 'Unknown'}
                  {selectedFile.path && ` | Path: ${selectedFile.path}`}
                </small>
              )}
            </div>
            
            <button 
              type="button" 
              className="btn btn-outline-primary btn-sm me-2"
              onClick={downloadFile}
              disabled={!fileContent}
            >
              <i className="fa fa-download me-1"></i>
              Download
            </button>
            
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
    </div>
    </>
  );
};

export default FileModal;